/**
 * Shown when the application cannot start.
 *
 * Phase 1's only startup failure mode is invalid environment configuration.
 * The screen prints the validation issues because they name variables, never
 * values — `EnvironmentValidationError` is built from names only, so this is
 * safe to render even in a pilot build.
 */

export interface BootstrapErrorProps {
  readonly title: string
  readonly issues: readonly string[]
}

export function BootstrapError({
  title,
  issues,
}: BootstrapErrorProps): React.JSX.Element {
  return (
    <main className="mx-auto flex min-h-dvh max-w-2xl flex-col justify-center px-6 py-12">
      <h1 className="text-2xl font-semibold tracking-tight text-rose-700 dark:text-rose-400">
        {title}
      </h1>
      <p className="mt-2 text-slate-600 dark:text-slate-400">
        The application stopped before starting. Fix the configuration and reload.
      </p>

      {issues.length > 0 && (
        <ul className="mt-6 space-y-2 rounded-lg border border-rose-200 bg-rose-50 p-5 dark:border-rose-900 dark:bg-rose-950/40">
          {issues.map((issue) => (
            <li
              key={issue}
              className="font-mono text-sm text-rose-900 dark:text-rose-200"
            >
              {issue}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-6 text-sm text-slate-500 dark:text-slate-500">
        See <code>.env.example</code> for the expected variables.
      </p>
    </main>
  )
}
